import React, { useState } from 'react';
import { PaymentRecord, GroupMemberProfile, GroupSettings, Match } from '../types';
import { DollarSign, CheckCircle2, AlertCircle, Copy, Check, Sparkles, Wallet } from 'lucide-react';

interface PaymentsViewProps {
  payments: PaymentRecord[];
  membersMap: Map<string, GroupMemberProfile>;
  group: GroupSettings;
  matches: Match[];
  isOrganizer: boolean;
  onMarkPayment: (matchId: string, memberProfileId: string, status: 'paid' | 'unpaid') => void;
}

export const PaymentsView: React.FC<PaymentsViewProps> = ({
  payments,
  membersMap,
  group,
  matches,
  isOrganizer,
  onMarkPayment
}) => {
  const [copiedBkash, setCopiedBkash] = useState(false);
  const [copiedNagad, setCopiedNagad] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unpaid' | 'paid'>('all');

  const copyToClipboard = (text: string, type: 'bkash' | 'nagad') => {
    navigator.clipboard.writeText(text);
    if (type === 'bkash') {
      setCopiedBkash(true);
      setTimeout(() => setCopiedBkash(false), 2000);
    } else {
      setCopiedNagad(true);
      setTimeout(() => setCopiedNagad(false), 2000);
    }
  };

  const totalCollected = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = payments.filter(p => p.status === 'unpaid').reduce((sum, p) => sum + p.amount, 0);

  const filteredPayments = payments.filter(p => {
    if (filter === 'unpaid') return p.status === 'unpaid';
    if (filter === 'paid') return p.status === 'paid';
    return true;
  });

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      
      {/* Top Banner */}
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white">Turf Financial Ledger</h2>
          <p className="text-xs text-slate-400">Track match turf payments, bKash / Nagad transfers, and group balances</p>
        </div>

        <div className="flex gap-2">
          <div className="bg-slate-950 border border-slate-800 px-4 py-2 rounded-2xl text-center">
            <span className="text-[10px] text-slate-400 block uppercase font-bold">Collected</span>
            <span className="text-base font-black font-mono text-emerald-400">৳{totalCollected}</span>
          </div>
          <div className="bg-slate-950 border border-slate-800 px-4 py-2 rounded-2xl text-center">
            <span className="text-[10px] text-slate-400 block uppercase font-bold">Pending</span>
            <span className="text-base font-black font-mono text-amber-400">৳{totalPending}</span>
          </div>
        </div>
      </div>

      {/* Payment Gateway Copy Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-pink-950/40 via-slate-900 to-slate-950 border border-pink-500/30 p-4 rounded-2xl flex justify-between items-center">
          <div>
            <span className="text-xs font-bold text-pink-400 block uppercase">bKash Personal Number</span>
            <span className="text-lg font-black font-mono text-white">{group.finance.bkashNumber}</span>
          </div>
          <button
            onClick={() => copyToClipboard(group.finance.bkashNumber, 'bkash')}
            className="px-3 py-1.5 bg-pink-500/20 text-pink-300 font-bold text-xs rounded-xl border border-pink-500/30 flex items-center gap-1 hover:bg-pink-500/30 transition"
          >
            {copiedBkash ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            <span>{copiedBkash ? 'Copied' : 'Copy'}</span>
          </button>
        </div>

        <div className="bg-gradient-to-br from-orange-950/40 via-slate-900 to-slate-950 border border-orange-500/30 p-4 rounded-2xl flex justify-between items-center">
          <div>
            <span className="text-xs font-bold text-orange-400 block uppercase">Nagad Personal Number</span>
            <span className="text-lg font-black font-mono text-white">{group.finance.nagadNumber}</span>
          </div>
          <button
            onClick={() => copyToClipboard(group.finance.nagadNumber, 'nagad')}
            className="px-3 py-1.5 bg-orange-500/20 text-orange-300 font-bold text-xs rounded-xl border border-orange-500/30 flex items-center gap-1 hover:bg-orange-500/30 transition"
          >
            {copiedNagad ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            <span>{copiedNagad ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex gap-2 bg-slate-950 p-1.5 rounded-2xl border border-slate-800 text-xs font-bold w-fit">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-xl transition ${filter === 'all' ? 'bg-emerald-500 text-slate-950' : 'text-slate-400'}`}
        >
          All Records
        </button>
        <button
          onClick={() => setFilter('unpaid')}
          className={`px-3 py-1.5 rounded-xl transition ${filter === 'unpaid' ? 'bg-amber-500 text-slate-950' : 'text-slate-400'}`}
        >
          Unpaid / Debts
        </button>
        <button
          onClick={() => setFilter('paid')}
          className={`px-3 py-1.5 rounded-xl transition ${filter === 'paid' ? 'bg-emerald-500 text-slate-950' : 'text-slate-400'}`}
        >
          Cleared
        </button>
      </div>

      {/* Payments List Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
        <div className="p-4 bg-slate-950 border-b border-slate-800 text-xs font-extrabold uppercase text-slate-400 tracking-wider">
          Match Turf Ledger Records
        </div>

        <div className="divide-y divide-slate-800">
          {filteredPayments.map((p) => {
            const m = membersMap.get(p.memberProfileId);
            const matchObj = matches.find(item => item.id === p.matchId);
            const isPaid = p.status === 'paid';

            return (
              <div key={p.id} className="p-4 flex items-center justify-between text-xs hover:bg-slate-800/40 transition">
                <div className="flex items-center gap-3">
                  <img src={m?.avatar} alt={m?.displayName} className="w-9 h-9 rounded-full object-cover border border-slate-700" />
                  <div>
                    <span className="font-extrabold text-white text-sm block">{m?.displayName}</span>
                    <span className="text-[10px] text-slate-400">{matchObj?.title || 'Turf Match'} • {p.paymentMethod}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="font-black font-mono text-sm text-white">৳{p.amount}</span>
                  <span className={`px-3 py-1 rounded-full font-bold font-mono text-[10px] ${
                    isPaid ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {isPaid ? 'PAID' : 'UNPAID'}
                  </span>

                  {isOrganizer && (
                    <button
                      onClick={() => onMarkPayment(p.matchId, p.memberProfileId, isPaid ? 'unpaid' : 'paid')}
                      className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] font-bold rounded-xl border border-slate-700 transition"
                    >
                      Toggle
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};
